import React, { useState, useMemo } from 'react';
import { Paper, Box, Chip, IconButton, TextField, Collapse, Typography, Button, FormControlLabel, Switch, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { DragIndicator, Delete, ExpandMore, ExpandLess, Add } from '@mui/icons-material';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import {
  ClientField,
  ClientFieldMultipleChoice,
  ClientFieldImageChoice,
  ClientFieldVideo,
  ClientFieldAppleMusicPlaylist
} from '../../../../shared/types/appTypes';
import { getFieldTypeOption } from '../types';
import FieldTypeIcon from './FieldTypeIcon';
import ImageChoiceEditor from './ImageChoiceEditor';
import { AUTO_POPULATE_OPTIONS } from 'src/constants/appConstants';

interface FormFieldItemProps {
  field: ClientField;
  index: number;
  templateId: string;
  onUpdate: (index: number, updates: Partial<ClientField>) => void;
  onDelete: (index: number) => void;
  onAddChoice: (fieldIndex: number) => void;
  onUpdateChoice: (fieldIndex: number, choiceIndex: number, value: string) => void;
  onDeleteChoice: (fieldIndex: number, choiceIndex: number) => void;
}

const FormFieldItem: React.FC<FormFieldItemProps> = ({
  field,
  index,
  templateId,
  onUpdate,
  onDelete,
  onAddChoice,
  onUpdateChoice,
  onDeleteChoice,
}) => {
  const [expanded, setExpanded] = useState(false);
  const fieldOption = getFieldTypeOption(field.type);

  const autoPopulateOptions = useMemo(
    () => AUTO_POPULATE_OPTIONS.filter((opt) => opt.compatibleTypes.includes(field.type)),
    [field.type]
  );

  return (
    <Draggable draggableId={`field-${index}`} index={index}>
      {(provided, snapshot) => (
        <Paper
          ref={provided.innerRef}
          {...provided.draggableProps}
          elevation={snapshot.isDragging ? 8 : 1}
          sx={{
            mb: 1.5,
            transition: 'box-shadow 0.2s',
            bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper'
          }}
        >
          {/* Header Row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              gap: 1.5
            }}
          >
            {/* Drag Handle */}
            <Box
              {...provided.dragHandleProps}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'grab',
                color: 'text.secondary',
                '&:active': { cursor: 'grabbing' }
              }}
            >
              <DragIndicator />
            </Box>

            {/* Field Type Icon & Chip */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
              <FieldTypeIcon type={field.type} color="primary" />
              <Chip label={fieldOption?.label || field.type} size="small" variant="outlined" color="primary" />
            </Box>

            {/* Label Preview */}
            <Typography
              variant="body1"
              sx={{
                flex: 1,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {field.label || 'Untitled Field'}
            </Typography>

            {/* Actions */}
            <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ ml: 'auto' }}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <IconButton size="small" color="error" onClick={() => onDelete(index)}>
              <Delete />
            </IconButton>
          </Box>

          {/* Expanded Edit Section */}
          <Collapse in={expanded}>
            <Box
              sx={{
                px: 2,
                pb: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                borderTop: 1,
                borderColor: 'divider',
                pt: 2
              }}
            >
              <TextField
                label="Field Label"
                value={field.label}
                onChange={(e) => onUpdate(index, { label: e.target.value })}
                size="small"
                fullWidth
              />

              {field.type === 'video' ? (
                // Video fields: only playback URL
                <TextField
                  label="Playback URL"
                  value={(field as ClientFieldVideo).playbackUrl || ''}
                  onChange={(e) => onUpdate(index, { playbackUrl: e.target.value } as Partial<ClientFieldVideo>)}
                  size="small"
                  fullWidth
                  placeholder="YouTube, Vimeo, or other video URL"
                  helperText="Supports YouTube, Vimeo, SoundCloud, Facebook, Twitch, and more"
                />
              ) : (
                // All other field types: common fields
                <>
                  <TextField
                    label="Question"
                    value={field.question}
                    onChange={(e) => onUpdate(index, { question: e.target.value })}
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                  />
                  <TextField
                    label="Helper Text"
                    value={field.helperText || ''}
                    onChange={(e) => onUpdate(index, { helperText: e.target.value })}
                    size="small"
                    fullWidth
                    placeholder="Optional hint for the user"
                  />
                  <TextField
                    label="Custom Response Text"
                    value={field.customResponseText || ''}
                    onChange={(e) => onUpdate(index, { customResponseText: e.target.value })}
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    helperText={
                      field.type !== 'appleMusicPlaylist' &&
                      "Optional text to display after saving (use [value] for the user's answer)"
                    }
                  />
                  <TextField
                    label="Custom Response Button Text"
                    value={field.customResponseButtonText || ''}
                    onChange={(e) => onUpdate(index, { customResponseButtonText: e.target.value })}
                    size="small"
                    fullWidth
                    helperText="Default: 'Ok, next question'"
                  />

                  {/* Auto Populate from Event */}
                  {autoPopulateOptions.length > 0 && (
                    <FormControl size="small" fullWidth>
                      <InputLabel>Auto Populate from Event</InputLabel>
                      <Select
                        label="Auto Populate from Event"
                        value={field.autoPopulateFrom || ''}
                        onChange={(e) =>
                          onUpdate(index, {
                            autoPopulateFrom: e.target.value || undefined,
                          } as Partial<ClientField>)
                        }
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {autoPopulateOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Type-specific fields */}
                  {field.type === 'appleMusicPlaylist' && (
                    <>
                      <TextField
                        label="Playlist URL"
                        value={(field as ClientFieldAppleMusicPlaylist).playlistUrl || ''}
                        onChange={(e) =>
                          onUpdate(index, { playlistUrl: e.target.value } as Partial<ClientFieldAppleMusicPlaylist>)
                        }
                        size="small"
                        fullWidth
                        placeholder="Apple Music playlist link"
                        helperText="Paste the Apple Music playlist URL"
                      />
                      <TextField
                        label="Min Choices"
                        type="number"
                        value={(field as ClientFieldAppleMusicPlaylist).minChoices ?? ''}
                        onChange={(e) =>
                          onUpdate(index, {
                            minChoices: e.target.value ? parseInt(e.target.value, 10) : undefined
                          } as Partial<ClientFieldAppleMusicPlaylist>)
                        }
                        size="small"
                        fullWidth
                        placeholder="Optional minimum number of songs"
                      />
                      <TextField
                        label="Max Choices"
                        type="number"
                        value={(field as ClientFieldAppleMusicPlaylist).maxChoices ?? ''}
                        onChange={(e) =>
                          onUpdate(index, {
                            maxChoices: e.target.value ? parseInt(e.target.value, 10) : undefined
                          } as Partial<ClientFieldAppleMusicPlaylist>)
                        }
                        size="small"
                        fullWidth
                        placeholder="Optional maximum number of songs"
                      />
                    </>
                  )}

                  {field.type === 'multipleChoice' && (
                    <Box>
                      <FormControlLabel
                        control={(
                          <Switch
                            size="small"
                            checked={(field as ClientFieldMultipleChoice).checkAllThatApply ?? false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const currentValue = (field as ClientFieldMultipleChoice).value;
                              const nextValue = checked
                                ? Array.isArray(currentValue)
                                  ? currentValue
                                  : currentValue
                                    ? [currentValue]
                                    : []
                                : Array.isArray(currentValue)
                                  ? currentValue[0] || null
                                  : currentValue;

                              onUpdate(index, {
                                checkAllThatApply: checked,
                                value: nextValue,
                              } as Partial<ClientFieldMultipleChoice>);
                            }}
                          />
                        )}
                        label="Check all that apply"
                        sx={{ mb: 0.5 }}
                      />

                      <FormControlLabel
                        control={(
                          <Switch
                            size="small"
                            checked={(field as ClientFieldMultipleChoice).allowOtherChoice ?? false}
                            onChange={(e) =>
                              onUpdate(index, {
                                allowOtherChoice: e.target.checked,
                              } as Partial<ClientFieldMultipleChoice>)
                            }
                          />
                        )}
                        label="Allow “Other” choice"
                        sx={{ mb: 1 }}
                      />

                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Choices ({(field as ClientFieldMultipleChoice).choices?.length || 0})
                      </Typography>

                      <Droppable droppableId={`multiple-choice-options-${index}`} type="MULTIPLE_CHOICE_OPTION">
                        {(dropProvided) => (
                          <Box ref={dropProvided.innerRef} {...dropProvided.droppableProps} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {((field as ClientFieldMultipleChoice).choices || []).map((choice, choiceIndex) => (
                              <Draggable
                                key={`field-${index}-choice-${choiceIndex}`}
                                draggableId={`field-${index}-choice-${choiceIndex}`}
                                index={choiceIndex}
                              >
                                {(choiceProvided, choiceSnapshot) => (
                                  <Paper
                                    ref={choiceProvided.innerRef}
                                    {...choiceProvided.draggableProps}
                                    variant="outlined"
                                    sx={{
                                      p: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      bgcolor: choiceSnapshot.isDragging ? 'action.hover' : 'background.paper',
                                    }}
                                  >
                                    <Box
                                      {...choiceProvided.dragHandleProps}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'grab',
                                        color: 'text.secondary',
                                        '&:active': { cursor: 'grabbing' },
                                      }}
                                    >
                                      <DragIndicator fontSize="small" />
                                    </Box>
                                    <TextField
                                      value={choice}
                                      onChange={(e) => onUpdateChoice(index, choiceIndex, e.target.value)}
                                      size="small"
                                      fullWidth
                                      placeholder={`Choice ${choiceIndex + 1}`}
                                    />
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => onDeleteChoice(index, choiceIndex)}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Paper>
                                )}
                              </Draggable>
                            ))}
                            {dropProvided.placeholder}
                          </Box>
                        )}
                      </Droppable>

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Add />}
                        onClick={() => onAddChoice(index)}
                        sx={{ mt: 1 }}
                      >
                        Add Choice
                      </Button>
                    </Box>
                  )}

                  {field.type === 'imageChoice' && (
                    <ImageChoiceEditor
                      options={(field as ClientFieldImageChoice).options || []}
                      onChange={(options) => onUpdate(index, { options } as Partial<ClientFieldImageChoice>)}
                      templateId={templateId}
                      fieldIndex={index}
                    />
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </Paper>
      )}
    </Draggable>
  );
};

export default FormFieldItem;
